import { useCallback, useEffect, useState } from "react";
import { saveInventoryOutlookSettings, type InventoryOutlookSettings } from "@/lib/manufacturing/inventoryMatchClient";
import { listOutlookBoard, placeOutlookUnit, type OutlookBoardRow } from "@/lib/manufacturing/outlookBoardClient";

const buttonClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-900 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100";

export function InventoryOutlookBoard({
  settings,
  onSettings,
}: {
  settings: InventoryOutlookSettings | null;
  onSettings: (settings: InventoryOutlookSettings) => void;
}) {
  const [query, setQuery] = useState("");
  const [units, setUnits] = useState<OutlookBoardRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dates, setDates] = useState<Record<string, string>>({});

  const load = useCallback(async (q: string) => {
    setBusy(true);
    setError(null);
    const out = await listOutlookBoard(q);
    setBusy(false);
    if (!out.ok) {
      setError(out.error);
      return;
    }
    if (!Array.isArray(out.data?.units)) {
      setError("The outlook board is not on this server yet.");
      return;
    }
    setUnits(out.data.units);
    setDates((prev) => {
      const next = { ...prev };
      for (const row of out.data.units) next[row.id] = row.build_on ?? next[row.id] ?? "";
      return next;
    });
  }, []);

  useEffect(() => {
    void load("");
  }, [load]);

  async function run(row: OutlookBoardRow, op: "next" | "build_on" | "deliver") {
    setBusy(true);
    setError(null);
    const out = await placeOutlookUnit({
      op,
      unit_id: row.id,
      build_on: op === "build_on" ? dates[row.id] ?? "" : undefined,
    });
    setBusy(false);
    if (!out.ok) {
      setError(out.error);
      return;
    }
    if (!Array.isArray(out.data?.units)) {
      setError("The outlook board is not on this server yet.");
      return;
    }
    if (query.trim()) await load(query);
    else setUnits(out.data.units);
  }

  async function toggleConsumption(on: boolean) {
    if (!settings) return;
    setError(null);
    const out = await saveInventoryOutlookSettings({ consume_on_final_qc: on });
    if (!out.ok) {
      setError(out.error);
      return;
    }
    onSettings(out.data.settings);
  }

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Place buildings on the board</h3>
      <p className="mt-1 text-sm text-gray-700 dark:text-zinc-300">
        Buildings that skip the kanban sit in 30 days. Move one phase ahead, or set a build date to pull that building into 7 or 14 days. A building marked ready for final QC lands on Final QA. Delivery, including a ShedSuite delivered status, takes it off the board.
      </p>
      <label className="mt-3 flex items-center gap-2 text-sm text-gray-900 dark:text-zinc-100">
        <input
          type="checkbox"
          checked={settings?.consume_on_final_qc === true}
          disabled={!settings || busy}
          onChange={(e) => void toggleConsumption(e.target.checked)}
        />
        Turn on materials consumption at final QC
      </label>
      <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
        Leave this off to keep tracking by inventory reconciliation. When it is on, a building that reaches Final QA issues its recipe materials.
      </p>
      <form
        className="mt-3 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void load(query);
        }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a serial"
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-950"
        />
        <button type="submit" className={buttonClass} disabled={busy}>
          {busy ? "Working…" : "Search"}
        </button>
      </form>
      {error ? <p className="mt-2 text-sm text-red-800 dark:text-red-200">{error}</p> : null}
      {error ? null : units.length === 0 ? (
        <p className="mt-3 text-sm text-gray-700 dark:text-zinc-300">
          {query.trim() ? "No open building matches that serial." : "No pass-through buildings are waiting."}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {units.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700"
            >
              <span className="min-w-0 text-gray-900 dark:text-zinc-100">
                <span className="font-medium">{row.serial}</span>
                <span> · {row.phase_name}</span>
                <span> · {row.horizon_days} days</span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={dates[row.id] ?? ""}
                  onChange={(e) => setDates((prev) => ({ ...prev, [row.id]: e.target.value }))}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-950"
                />
                <button type="button" className={buttonClass} disabled={busy} onClick={() => void run(row, "build_on")}>
                  Apply date
                </button>
                <button type="button" className={buttonClass} disabled={busy} onClick={() => void run(row, "next")}>
                  Next phase
                </button>
                <button type="button" className={buttonClass} disabled={busy} onClick={() => void run(row, "deliver")}>
                  Delivered
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
